/** Delivery / Aggregators module — Uber Eats & DoorDash mix, margins, ratings. */
import React from 'react';
import { Bike, Clock, Percent, Star, ChevronRight, Sparkles } from 'lucide-react';
import KpiTile from './KpiTile';
import Heatmap from './Heatmap';
import ChannelProfit from './ChannelProfit';
import CoryTips from '@/components/demo/CoryTips';
import { QSR_STORES, DAYPARTS, avgBy } from './qsrDemoData';

const net = (k) => QSR_STORES.reduce((s, x) => s + x.delivery[k], 0) / QSR_STORES.length;
const DELIVERY_SKEW = [0.5, 0.7, 0.95, 0.9, 1.35, 1.6, 1.15];

const totalSales = QSR_STORES.reduce((s, x) => s + x.weeklySales, 0);
const deliverySales = QSR_STORES.reduce((s, x) => s + x.weeklySales * (x.delivery.mix / 100), 0);
const networkMixPct = (deliverySales / totalSales) * 100;

export default function DeliveryModule({ onSelectStore, onNavigate }) {
  const mix = net('mix');
  const time = net('avgDeliveryMin');
  const margin = net('marginPct');
  const rating = net('uberRating');
  const heat = QSR_STORES.map((s) => s.salesByDaypart.map((v, i) => Math.round(v * DELIVERY_SKEW[i])));

  // Arriving via a Cory deep-link (/app/dashboard?tab=delivery#pickup-lever): scroll to + flash the lever.
  React.useEffect(() => {
    if (typeof window === 'undefined' || window.location.hash !== '#pickup-lever') return;
    const t = setTimeout(() => {
      const el = document.getElementById('pickup-lever');
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('pickup-flash');
      setTimeout(() => el.classList.remove('pickup-flash'), 1800);
      window.dispatchEvent(new CustomEvent('qsr-pickup-nudge'));
    }, 500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="space-y-4 qsr-stage p-3 sm:p-5" data-testid="delivery-module">
      <CoryTips page="delivery" variant="qsr" onAction={onNavigate} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={Bike} label="Delivery mix" value={`${mix.toFixed(0)}%`} sub="of total orders" delta="+2%" deltaGood color="#8b5cf6" trend={[22, 24, 25, 27, 28, 30, 31, 32, 33, 33, 34, mix]} testid="delivery-kpi-mix" source="Doshii · Uber Eats + DoorDash" ai={`Delivery is ${mix.toFixed(0)}% of orders and climbing. It's most profitable at lunch — protect margin at the dinner/late peak where aggregator fees bite hardest.`} />
        <KpiTile icon={Clock} label="Avg delivery time" source="Doshii · Uber Eats + DoorDash" value={`${time.toFixed(0)}m`} sub="handover to door" delta="-2m" deltaGood color="#3b82f6" />
        <KpiTile icon={Percent} label="Delivery margin" source="Doshii · after aggregator fees" value={`${margin.toFixed(0)}%`} sub="after aggregator fees" delta="-1%" deltaGood={false} color="#f59e0b" ai={`Delivery margin is ${margin.toFixed(0)}% after fees — well under dine-in. Nudging pickup with a loyalty offer at the late day-part is the cleanest margin recovery.`} />
        <KpiTile icon={Star} label="Aggregator rating" source="Uber Eats + DoorDash" value={rating.toFixed(1)} sub="Uber Eats avg" color="#10b981" />
      </div>

      <div id="pickup-lever" className="scroll-mt-24">
        <ChannelProfit sales={totalSales} mixPct={networkMixPct} subtitle="Weekly · network · Doshii + Lightspeed" showSlider />
      </div>

      <Heatmap
        rows={QSR_STORES.map((s) => s.name)}
        cols={DAYPARTS}
        values={heat}
        title="Delivery demand by day-part"
        ai="Delivery demand concentrates at the dinner and late day-parts — the exact windows where speed of service is already tightest. Staff the kitchen for combined dine-in + delivery load there."
      />

      <div className="rounded-2xl qsr-card shadow-sm divide-y divide-slate-100">
        {QSR_STORES.map((s) => (
          <button key={s.id} onClick={() => onSelectStore?.(s)} className="w-full text-left flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors group" data-testid={`delivery-row-${s.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm text-slate-900">{s.name}</div>
              <div className="flex gap-3 mt-1 flex-wrap text-[11px] text-slate-500">
                <span>Mix <b className="text-slate-800">{s.delivery.mix}%</b></span>
                <span>Margin <b className="text-slate-800">{s.delivery.marginPct}%</b></span>
                <span className="inline-flex items-center gap-0.5">Uber <b className="text-slate-800">{s.delivery.uberRating}</b><Star className="w-3 h-3 text-amber-400 fill-amber-400" /></span>
                <span className="inline-flex items-center gap-0.5">DoorDash <b className="text-slate-800">{s.delivery.doordashRating}</b><Star className="w-3 h-3 text-amber-400 fill-amber-400" /></span>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
        <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-700 leading-relaxed">
          <span className="font-semibold text-slate-900">Corvana AI:</span> Southport's delivery margin ({QSR_STORES[2].delivery.marginPct}%)
          is well below the {margin.toFixed(0)}% network average while carrying the highest mix ({QSR_STORES[2].delivery.mix}%).
          Renegotiating the aggregator tier or nudging pickup with a loyalty offer protects margin as delivery grows.
        </p>
      </div>
    </div>
  );
}
